<template>
  <el-dialog
    title="Invoice Preview"
    :visible.sync="visible"
    width="800px"
    :close-on-click-modal="false"
    append-to-body="true"
    @close="handleClose"
  >
    <div class="invoice-preview" ref="invoicePreview">
                <div class="invoice-header">
                    <div class="logo-section">CMOFFER</div>
                    <div class="invoice-title-section">
                        <h1 class="invoice-title">Tax Invoice</h1>
                    </div>
                </div>
                
                <div class="invoice-details-container">
                    <div class="from-to-section">
                        <div class="from-section">
                            <div class="section-label">From</div>
                            <div class="company-name">{{ currentInvoice.from_company }}</div>
                            <div class="address-line">{{ currentInvoice.from_address }}</div>
                            <div class="address-line">{{ currentInvoice.from_city }}, {{ currentInvoice.from_state }} {{ currentInvoice.from_zip }}</div>
                            <div class="address-line">{{ currentInvoice.from_country }}</div>
                            <div v-if="currentInvoice.from_tax_number" class="tax-number">Tax ID: {{ currentInvoice.from_tax_number }}</div>
                        </div>
                        
                        <div class="to-section">
                            <div class="section-label">Bill To</div>
                            <div class="company-name">{{ currentInvoice.to_name }}</div>
                            <div class="address-line">{{ currentInvoice.to_address }}</div>
                            <div class="address-line">{{ currentInvoice.to_city }}, {{ currentInvoice.to_state }} {{ currentInvoice.to_zip }}</div>
                            <div class="address-line">{{ currentInvoice.to_country }}</div>
                        </div>
                    </div>
                    
                    <div class="invoice-info-section">
                        <div class="invoice-info-item">
                            <div class="info-label">Invoice Number</div>
                            <div class="info-value">{{ currentInvoice.invoice_no }}</div>
                        </div>
                        <div class="invoice-info-item">
                            <div class="info-label">Invoice Date</div>
                            <div class="info-value">{{ formatDate(currentInvoice.issue_date) }}</div>
                        </div>
                        <div class="invoice-info-item">
                            <div class="info-label">Invoice Period</div>
                            <div class="info-value">{{ formatDate(currentInvoice.billing_start_date) }} - {{ formatDate(currentInvoice.billing_end_date) }}</div>
                        </div>
                        <div class="invoice-info-item">
                            <div class="info-label">Invoice Currency</div>
                            <div class="info-value">{{ currentInvoice.currency }}</div>
                        </div>
                    </div>
                </div>
                
                <div class="invoice-body">
                    <table class="invoice-table">
                        <thead>
                            <tr>
                                <th style="width: 70%;">Description</th>
                                <th style="width: 30%; text-align: right;">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(item, index) in currentInvoice.items" :key="index">
                                <td>{{ item.description }}</td>
                                <td style="text-align: right;">{{ '$' + formatAmount(item.amount) }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr class="subtotal-row">
                                <td>Subtotal</td>
                                <td style="text-align: right;">{{ '$' + formatAmount(currentInvoice.subtotal || currentInvoice.total_amount) }}</td>
                            </tr>
                            <tr class="tax-row">
                                <td>Tax ({{ currentInvoice.tax_rate || '0' }}%)</td>
                                <td style="text-align: right;">{{ '$' + formatAmount(currentInvoice.tax) }}</td>
                            </tr>
                            <tr class="total-row">
                                <td>Tax Subtotal</td>
                                <td style="text-align: right;">{{ '$' + formatAmount(currentInvoice.tax) }}</td>
                            </tr>
                            <tr class="total-row">
                                <td>Total Amount Due</td>
                                <td style="text-align: right;">{{ '$' + formatAmount(currentInvoice.total_amount) }}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                
                <div class="invoice-footer">
                    <div class="footer-content">
                        <p>Thank you for your business!</p>
                        <p>If you have any questions, please contact our customer support team.</p>
                    </div>
                </div>
            </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Cancel</el-button>
      <el-button type="primary" @click="exportToPDF" :loading="exporting">
        <i class="el-icon-download"></i>
        Download PDF
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import tools from "@/utils/tools.js";

export default {
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    currentInvoice: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      exporting: false
    }
  },
  methods: {
    handleClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    formatAmount(amount) {
      if (amount === null || amount === undefined || amount === "") {
        return "0.00";
      }
      return parseFloat(amount).toFixed(2);
    },
    formatDate(date) {
      return tools.formatDate(date);
    },
    async exportToPDF() {
      this.exporting = true;
      try {
        const element = this.$refs.invoicePreview;
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
        });
        
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const imgWidth = canvas.width;
        const imgHeight = canvas.height;
        const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
        const imgX = (pdfWidth - imgWidth * ratio) / 2;
        const imgY = 10;
        
        pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
        pdf.save(`Invoice_${this.currentInvoice.invoice_no}.pdf`);
        
        this.$message.success(this.$t('Download successful'));
        this.$emit('update:visible', false);
      } catch (error) {
        console.error('PDF export error:', error);
        this.$message.error(this.$t('Download failed'));
      } finally {
        this.exporting = false;
      }
    }
  }
}
</script>

<style scoped lang="scss">
.invoice-preview {
    padding: 20px;
    background: #fff;
    font-family: Arial, sans-serif;
    color: #333;

    .invoice-header {
        margin-bottom: 30px;
        padding-bottom: 20px;
        display: flex;
        justify-content: space-between;
        align-items: flex-end;

        .logo-section {
            font-size: 36px;
            font-weight: bold;
            color: #333;
        }

        .invoice-title-section {
            .invoice-title {
                font-size: 24px;
                font-weight: bold;
                color: #333;
                margin: 0;
                text-align: right;
            }
        }
    }

    .invoice-details-container {
        margin-bottom: 40px;

        .from-to-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 30px;
            gap: 40px;

            .from-section, .to-section {
                flex: 1;

                .section-label {
                    font-size: 16px;
                    font-weight: bold;
                    color: #333;
                    margin-bottom: 10px;
                    display: block;
                }

                .company-name {
                    font-size: 14px;
                    font-weight: 600;
                    color: #333;
                    margin-bottom: 5px;
                }

                .address-line {
                    font-size: 14px;
                    color: #666;
                    margin-bottom: 3px;
                }

                .tax-number {
                    font-size: 14px;
                    color: #666;
                    margin-top: 5px;
                }
            }
        }

        .invoice-info-section {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;
            border: 1px solid #e0e0e0;
            padding: 16px 10px;
            background-color: #f9f9f9;

            .invoice-info-item {
                flex: 0 0 calc(50% - 4px);
                min-width: calc(50% - 4px);

                .info-label {
                    font-size: 14px;
                    color: #666;
                    margin-right: 8px;
                    display: inline-block;
                    width: 120px;
                }

                .info-value {
                    font-size: 14px;
                    font-weight: 600;
                    color: #333;
                    display: inline-block;
                }
            }
        }
    }

    .invoice-body {
        margin-bottom: 40px;

        .invoice-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 14px;

            thead {
                tr {
                    th {
                        padding: 15px;
                        text-align: left;
                        background: #f5f5f5;
                        border-bottom: 2px solid #ddd;
                        font-weight: bold;
                        color: #333;
                    }
                }
            }

            tbody {
                tr {
                    td {
                        padding: 15px;
                        border-bottom: 1px solid #e0e0e0;
                        color: #666;
                    }
                }
            }

            tfoot {
                tr {
                    td {
                        padding: 15px;
                        font-weight: 600;
                        color: #333;
                    }
                }

                .subtotal-row, .tax-row {
                    font-size: 14px;
                    border-top: 1px solid #e0e0e0;
                }

                .total-row {
                    font-size: 16px;
                    font-weight: bold;
                    border-top: 2px solid #333;
                }
            }
        }
    }

    .invoice-footer {
        text-align: center;
        padding-top: 20px;
        border-top: 1px solid #e0e0e0;

        .footer-content {
            font-size: 14px;
            color: #666;
            line-height: 1.5;

            p {
                margin: 5px 0;
            }
        }
    }
}
</style>